import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';

/**
 * useFeriadosChile — obtiene feriados chilenos desde el backend (colección config_feriados).
 *
 * Cachea en memoria por años solicitados, dedupea llamadas concurrentes, y
 * expone helpers `isFeriado(iso)` / `getFeriadoInfo(iso)` / `isBloqueado(iso)`.
 *
 * Uso típico:
 *   const { isBloqueado, getFeriadoInfo } = useFeriadosChile();
 *
 * Por defecto pide el año actual + próximo año (el rango típico del grid 91 días
 * puede cruzar fin de año).
 */
const _CACHE = new Map(); // key: "2025,2026" → { feriados: [...], loadedAt }
const _INFLIGHT = new Map();

export function useFeriadosChile(years) {
  const now = new Date();
  const defaultYears = useMemo(
    () => years || [now.getFullYear(), now.getFullYear() + 1],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [years?.join(',')],
  );
  const cacheKey = defaultYears.join(',');
  const [feriados, setFeriados] = useState(() => _CACHE.get(cacheKey)?.feriados || []);
  const [loading, setLoading] = useState(!_CACHE.has(cacheKey));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (_CACHE.has(cacheKey)) {
        setFeriados(_CACHE.get(cacheKey).feriados);
        setLoading(false);
        return;
      }
      // Dedup concurrent calls for same key
      if (_INFLIGHT.has(cacheKey)) {
        const list = await _INFLIGHT.get(cacheKey);
        if (!cancelled) {
          setFeriados(list);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      const p = (async () => {
        try {
          const res = await api.get(`/config/feriados/all?anios=${cacheKey}`);
          const list = res.data?.feriados || [];
          _CACHE.set(cacheKey, { feriados: list, loadedAt: Date.now() });
          return list;
        } catch {
          _CACHE.set(cacheKey, { feriados: [], loadedAt: Date.now() });
          return [];
        } finally {
          _INFLIGHT.delete(cacheKey);
        }
      })();
      _INFLIGHT.set(cacheKey, p);
      const list = await p;
      if (!cancelled) {
        setFeriados(list);
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [cacheKey]);

  const byDate = useMemo(() => {
    const map = {};
    for (const f of feriados) map[f.fecha] = f;
    return map;
  }, [feriados]);

  const isFeriado = (iso) => !!byDate[iso];
  const getFeriadoInfo = (iso) => byDate[iso] || null;

  // Convierte una fecha (Date o ISO) a ISO 'YYYY-MM-DD' local
  const toIso = (d) => {
    if (!d) return '';
    if (typeof d === 'string') return d.slice(0, 10);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // ¿La fecha está bloqueada (sábado/domingo/feriado)?
  const isBloqueado = (d) => {
    if (!d) return false;
    const date = typeof d === 'string' ? new Date(d + 'T12:00:00') : d;
    const wd = date.getDay(); // 0=dom, 6=sab
    if (wd === 0 || wd === 6) return true;
    return isFeriado(toIso(date));
  };

  // Razón por la que está bloqueada (para tooltips)
  const getBloqueoReason = (d) => {
    if (!d) return null;
    const date = typeof d === 'string' ? new Date(d + 'T12:00:00') : d;
    const wd = date.getDay();
    if (wd === 0) return 'Domingo';
    if (wd === 6) return 'Sábado';
    const info = getFeriadoInfo(toIso(date));
    if (info) return `Feriado: ${info.nombre}${info.irrenunciable ? ' (irrenunciable)' : ''}`;
    return null;
  };

  return { feriados, loading, isFeriado, getFeriadoInfo, isBloqueado, getBloqueoReason };
}

export default useFeriadosChile;
